import { EstimateStatus } from "features/taskAndClaims/taskAndClaims.types";
import { z } from "zod";

import {
  FollowUpIntervals,
  ProjectRegisterClientType,
} from "./projectRegister.enums";
import { schemaMessages } from "./projectRegister.intl";
import {
  calculateItemPriceRecursive,
  validateNestingDepth,
} from "./projectRegister.utils";

import {
  OptionTypeConstants,
  PRICE_MAX,
  THREE_HUNDRED,
  TWENTY,
} from "@/app/constants/constants";
import { MultiSelectDefaultValuesSchema } from "@/app/schemas/general.schema";
import { createTranslator } from "@/app/utils/intlMessage.utils";
import { Any } from "@/interfaces/globals";
import {
  alphanumericValidator,
  basicValidator,
  dateValidator,
  numberValidator,
  reactSelectValidator,
  singleSelectValidator,
} from "@/utils/core/validators";
import { preprocessEmptyOrNullToUndefined } from "@/utils/form/formHelper";

const t = createTranslator(schemaMessages);

export const ProjectRegisterAddressSchema = z.object({
  longitude: z.string().min(3),
  latitude: z.string().min(3),
  label: z.string().min(1),
  areaLocation: z.string().optional(),
});

export const ProjectRegisterSchema = z.object({
  projectDetails: z.discriminatedUnion(
    "type",
    [
      z.object({
        type: z.literal(ProjectRegisterClientType.CUSTOMER),
        customerId: z.string().uuid({ message: t("selectCustomer") }),
        defaultCustomerName: z.string().optional(),
        customerUserId: reactSelectValidator(t("selectCustomerUser"), true),
        defaultCustomerUserNames: MultiSelectDefaultValuesSchema,
        prospectId: z.string().optional(),
        prospectUserId: z.any().optional(),
      }),
      z.object({
        type: z.literal(ProjectRegisterClientType.PROSPECT),
        prospectId: z.string().uuid({ message: t("selectProspect") }),
        defaultProspectName: z.string().optional(),
        prospectUserId: reactSelectValidator(t("selectProspectUser"), true),
        defaultProspectUserNames: MultiSelectDefaultValuesSchema,
        customerId: z.string().optional(),
        customerUserId: z.any().optional(),
      }),
    ],
    {
      errorMap: (issue, ctx) => {
        if (issue.code === "invalid_union_discriminator") {
          return { message: t("selectClientType") };
        }

        return { message: ctx.defaultError };
      },
    },
  ),
  referenceNumber: alphanumericValidator({
    allowHyphen: true,
    allowUnderscore: true,
    required: false,
    allowSpace: true,
  }),

  projectNumber: alphanumericValidator({
    allowHyphen: true,
    required: false,
    allowSpace: true,
  }).transform((value) => (!value?.length ? null : value)),
  projectName: basicValidator(true, t("required"), 1),
  dueDate: dateValidator(true, t("dueDateRequired")),
  wonDate: dateValidator(false, t("wonDateError")),
  quoteSubmission: dateValidator(false, t("submissionDateError")),
  receivedDate: dateValidator(true, t("dateRequired")),
  address: ProjectRegisterAddressSchema,
  buildersNo: z.coerce
    .number({
      invalid_type_error: t("invalidBuilderNo"),
    })
    .max(9999999)
    .optional(),
  quoteValue: numberValidator({
    required: false,
  }),
  ourTeam: reactSelectValidator(t("selectTeam"), true),
  ourTeamDefaultValue: MultiSelectDefaultValuesSchema,
  constructionType: z
    .string()
    .uuid({ message: t("selectConstructionType") })
    .optional(),
  dealDescription: z.array(z.string()).optional(),
  estimationType: singleSelectValidator({
    required: false,
  }),
  source: singleSelectValidator({
    required: false,
  }),
  inspectors: reactSelectValidator(t("selectInspectors"), true),
  inspectorsDefaultValue: MultiSelectDefaultValuesSchema,
  inspectionDate: dateValidator(false).nullable(),
  siteInspection: singleSelectValidator({
    required: false,
  }),
  status: z.string().optional(),
});

export const PartialProjectRegisterSchema = ProjectRegisterSchema.partial();

const QuantitySchema = z.preprocess(
  preprocessEmptyOrNullToUndefined,
  z.coerce
    .number({
      required_error: t("quantityRequired"),
      invalid_type_error: t("quantityRequired"),
    })
    .min(1, { message: t("quantityRequired") }), // Also reject 0
);

const PriceSchema = z.preprocess(
  preprocessEmptyOrNullToUndefined,
  z.coerce.number({
    required_error: t("priceRequired"),
    invalid_type_error: t("priceRequired"),
  }),
);

const DraftPriceSchema = z.preprocess(
  preprocessEmptyOrNullToUndefined,
  z.coerce
    .number({
      invalid_type_error: t("priceRequired"),
    })
    .optional(),
);

// Recursive item schema with nested subItems support (up to 5 levels)
const ItemDetailSchema: z.ZodType<Any> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      item: basicValidator(true, t("itemNameRequired"), 1, 5000),
      description: z
        .string({ message: t("descriptionRequired") })
        .trim()
        .optional(),
      position: z.coerce.number().min(0),
      unit: z.string().optional(),
      quantity: QuantitySchema,
      price: PriceSchema,
      margin: numberValidator({
        min: -100,
        required: false,
        maxChars: Number.MAX_SAFE_INTEGER,
      }).optional(),
      total: numberValidator({
        maxChars: Number.MAX_SAFE_INTEGER,
        max: PRICE_MAX,
        messages: {
          required: t("totalRequired"),
          min: t("totalCannotBeZero"),
          max: t("totalExceedMax"),
        },
      }),
      isNested: z.boolean().optional(),
      isExpanded: z.boolean().optional(),
      note: z.string().optional(),
      subItems: z.array(ItemDetailSchema).optional(),
    })
    .superRefine((data, ctx) => {
      // Validate nesting depth doesn't exceed 5 levels
      if (!validateNestingDepth(data, 0, 5)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("nestingDepthError"),
          path: ["subItems"],
        });
      }
    }),
);

// Draft version with optional fields (for form usage)
const DraftItemDetailSchema: z.ZodType<Any> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      item: basicValidator(true, t("itemNameRequired"), 1, 5000),
      description: z
        .string({ message: t("descriptionRequired") })
        .trim()
        .optional(),
      position: z.coerce.number().min(0).optional(),
      unit: z.string().optional(),
      quantity: z.coerce
        .number({ message: t("quantityRequired") })
        .optional()
        .default(1),
      price: DraftPriceSchema,
      margin: numberValidator({
        min: -100,
        required: false,
        maxChars: Number.MAX_SAFE_INTEGER,
      }).optional(),
      total: numberValidator({
        maxChars: Number.MAX_SAFE_INTEGER,
        max: PRICE_MAX,
        messages: {
          required: t("totalRequired"),
          min: t("totalCannotBeZero"),
          max: t("totalExceedMax"),
        },
      }).optional(),
      isNested: z.boolean().optional(),
      isExpanded: z.boolean().optional(),
      note: z.string().optional(),
      subItems: z.array(DraftItemDetailSchema).optional(),
    })
    .superRefine((data, ctx) => {
      // Validate nesting depth doesn't exceed 5 levels
      if (!validateNestingDepth(data, 0, 5)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("nestingDepthError"),
          path: ["subItems"],
        });
      }
    }),
);

// Summary schema for estimation table
export const EstimationTableSummarySchema = z.object({
  discount: z.coerce.number().default(0),
  subTotal: z.coerce.number().default(0),
  margin: z.coerce.number().default(0),
  gst: z.coerce.number().default(0),
  exGstTotal: z.coerce.number().default(0),
  total: z.coerce.number().default(0),
});

// Draft estimation schema (for form usage with optional fields)
export const DraftEstimateCreationSchema = z
  .object({
    mainCost: z.array(DraftItemDetailSchema),
    optionalCost: z.array(DraftItemDetailSchema).optional(),
    summary: EstimationTableSummarySchema.partial(),
    note: basicValidator(false, "", 3, 2500).optional(),
    quoteVersion: z
      .string()
      .trim()
      .max(THREE_HUNDRED, {
        message: t("quoteVersionMax"),
      })
      .optional(),
    isDraft: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // Calculate subtotal from mainCost and optionalCost (recursively)
    let subTotal = 0;

    // Calculate from mainCost
    data.mainCost?.forEach((item) => {
      subTotal += calculateItemPriceRecursive(item, 0);
    });

    // Calculate from optionalCost
    data.optionalCost?.forEach((item) => {
      subTotal += calculateItemPriceRecursive(item, 0);
    });

    // Validate discount doesn't exceed subtotal
    const discount = data.summary?.discount || 0;
    if (discount > Math.abs(subTotal)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("cannotExceedSubtotal"),
        path: ["summary", "discount"],
      });
    }
  });

const EstimationItemCreationSchema = ItemDetailSchema;

export const MainEstimationSchema = z
  .object({
    note: basicValidator(false, "", 3, 2500),
    mainCost: z.array(EstimationItemCreationSchema),
    optionalCost: z.array(EstimationItemCreationSchema).optional(),
    quoteVersion: z
      .string()
      .trim()
      .max(THREE_HUNDRED, {
        message: t("quoteVersionMax"),
      })
      .optional(),
    summary: EstimationTableSummarySchema,
    isDraft: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // Calculate subtotal from mainCost and optionalCost (recursively)
    let subTotal = 0;

    // Calculate from mainCost
    data.mainCost?.forEach((item) => {
      subTotal += calculateItemPriceRecursive(item, 0);
    });

    // Calculate from optionalCost
    data.optionalCost?.forEach((item) => {
      subTotal += calculateItemPriceRecursive(item, 0);
    });

    // Validate discount doesn't exceed subtotal
    const discount = data.summary?.discount || 0;
    if (discount > Math.abs(subTotal)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("cannotExceedSubtotal"),
        path: ["summary", "discount"],
      });
    }
  });

export const ForOptionsSchema = z.union([
  z.literal(OptionTypeConstants.ESTIMATION_STATUS),
  z.literal(OptionTypeConstants.ESTIMATION_SUBMISSION_METHOD),
  z.literal(OptionTypeConstants.PO_STATUS),
  z.literal(OptionTypeConstants.CLAIM_STATUS),
  z.literal(OptionTypeConstants.INVOICE_METHOD),
  z.literal(OptionTypeConstants.TASK_AND_CLAIMS),
  z.literal(OptionTypeConstants.PROJECT_REGISTER_STATUS),
  z.literal(OptionTypeConstants.CONSTRUCTION_TYPE),
  z.literal(OptionTypeConstants.ESTIMATION_TYPE),
  z.literal(OptionTypeConstants.SOURCE),
  z.literal(OptionTypeConstants.INSPECTION),
  z.literal(OptionTypeConstants.DEAL_DESCRIPTION),
  z.literal(OptionTypeConstants.PROJECT_REGISTER_SUBMISSION_STATUS),
]);

export const EOptionSchema = z.object({
  id: z.string().optional(),
  name: basicValidator(true, t("nameRequired"), 1, TWENTY),
  for: ForOptionsSchema,
  // for: z.enum(["ESTIMATION_STATUS", "ESTIMATION_SUBMISSION_METHOD"]),
  details: z.object({
    position: z.coerce.number({
      message: t("positionRequired"),
    }),
    textColor: z
      .string({ message: t("textColorRequired") })
      .min(3, { message: t("textColorRequired") }),
    textOpacity: z.coerce
      .number({ message: t("textOpacityRequired") })
      .min(0)
      .max(100),
    backgroundColor: z
      .string({
        message: t("backgroundColorRequired"),
      })
      .min(3, {
        message: t("backgroundColorRequired"),
      }),
    backgroundOpacity: z.coerce
      .number({
        message: t("backgroundOpacityRequired"),
      })
      .min(0)
      .max(100),
  }),
  workspaceId: z.string().uuid().optional(),
});

export const EstimateOptionSchema = z.object({
  data: z.array(EOptionSchema),
});

export const ClaimsOptionSchema = EOptionSchema.extend({
  for: z.union([
    z.literal("PO_STATUS"),
    z.literal("CLAIM_STATUS"),
    z.literal("INVOICE_METHOD"),
  ]),
});

export const FollowUpSchema = z.discriminatedUnion("isFollowUpRequired", [
  z.object({
    isFollowUpRequired: z.literal(false),
  }),
  z.object({
    isFollowUpRequired: z.literal(true),
    followUpDetails: z.object({
      id: z.string().optional(),
      teamMembers: reactSelectValidator().superRefine((data, ctx) => {
        if (!data) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("selectTeamMember"),
          });
        }
      }),
      intervals: z.nativeEnum(FollowUpIntervals, {
        message: t("followUpIntervalRequired"),
      }),
      startingDate: dateValidator(true, t("startingDateRequired")),
    }),
  }),
]);

export const EstimationBaseSchema = z.object({
  quoteVersion: z.string().max(300).optional(),
  quoteFile: z.string().optional(),
  amount: z.coerce.number(),
  status: basicValidator(true, t("statusRequired")),
  submittedDate: dateValidator(true, t("dateRequired")),
  submissionMethod: basicValidator(true, t("statusRequired")),
  estimationTable: MainEstimationSchema.optional(),
});

export const UpdateEstimationStatusSchema = z.object({
  estimateStatus: z.nativeEnum(EstimateStatus),
});

export const EstimatesReviewSchema = z.object({
  review: basicValidator(),
});

export const UserIdsSchema = z.object({
  Clients: reactSelectValidator(t("selectAtLeastOneUser"), true),
});
